import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  ChangeVehicleOwnershipRequestInterface,
  CreateCustomerVehicleRequest,
  ListCustomerVehiclesFilterParams,
  UpdateCustomerVehicleRequest,
} from './CustomerVehicles.types';

import {
  listCustomerVehicles,
  deleteCustomerVehicle,
  createCustomerVehicle,
  updateCustomerVehicle,
  getCustomerVehicle,
  changeCustomerVehicleOwnership,
  enrichCustomerVehicle,
} from './CustomerVehicles.service';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import replaceWords from '@/utils/functions/replaceWords';
import verifyIfHasVinculed from '@/utils/functions/verifyIfHasVinculed';

const errorsDicionary: Record<string, string> = {
  'Vehicle cannot be deleted due to relation.':
    'Não é possivel excluir o veiculo, pois ele já tem vínculo com outro módulo.',
};

export function useCustomerVehicles(
  customerId?: number,
  params?: ListCustomerVehiclesFilterParams,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['customers', customerId, 'vehicles']);

  const query = useQuery(
    ['customers', customerId, 'vehicles'],
    () => {
      return listCustomerVehicles(customerId, params);
    },
    {
      enabled:
        !!customerId &&
        hasPermissions([Authorities.REGISTER_CUSTOMER_VIEW]) &&
        !cache,

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('Invalid plate') ? 'Placa inválida' : err,
            ),
        });
      },
    },
  );

  return query;
}

export function useCustomerVehicle(
  customerId?: number,
  vehicleId?: number,
  enabled = true,
) {
  const query = useQuery(
    ['customers', customerId, 'vehicles', vehicleId],
    () => getCustomerVehicle(customerId, vehicleId),
    { enabled: !!customerId && !!vehicleId && enabled },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateCustomerVehicle(customerId?: number) {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreateCustomerVehicleRequest) =>
      customerId || obj?.customerId
        ? createCustomerVehicle((customerId || obj?.customerId) as number, obj)
        : Promise.resolve(null),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customers.vehicle.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'customers.vehicle.create.notification.successDescription',
          }),
        });

        queryChanel({
          invalidateQueries: [['customers', customerId, 'vehicles']],
          resetQueries: [
            ['customers', customerId, 'vehicles'],
            ['customer/filter/reduced'],
            ['customer/detail/reduced'],
            ['customer/filter'],
          ],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateCustomerVehicle(customerId?: number) {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: UpdateCustomerVehicleRequest) =>
      customerId
        ? updateCustomerVehicle(customerId, obj.id, obj)
        : Promise.resolve(null),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customers.vehicle.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'customers.vehicle.update.notification.successDescription',
          }),
        });

        queryChanel({
          invalidateQueries: [['customers', customerId, 'vehicles']],
          resetQueries: [
            ['customers', customerId, 'vehicles'],
            ['customer/filter/reduced'],
            ['customer/detail/reduced'],
            ['customer/detail/general'],
            ['customer/filter'],
          ],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteCustomerVehicle() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    ({ customerId, vehicleId }: { customerId: number; vehicleId: number }) =>
      deleteCustomerVehicle(customerId, vehicleId),
    {
      async onSuccess(_, data) {
        notification.success({
          message: 'Remoção de veículo',
          description: 'Veículo removido com sucesso!',
        });

        queryChanel({
          invalidateQueries: [
            ['customer/detail', data.customerId],
            ['customers', data.customerId, 'vehicles'],
          ],
          resetQueries: [
            ['customers', data.customerId, 'vehicles'],
            ['customer/filter/reduced'],
            ['customer/detail/general', data.customerId],
            ['customer/detail/general/mutation', data.customerId],
            ['customer/filter'],
            ['customer/detail/reduced'],
          ],
        });
      },

      onError(error) {
        notificationError(error, {
          message: 'Impossibilidade de exclusão',
          translate: (errors) =>
            errors.map((err) =>
              errorsDicionary[err] || verifyIfHasVinculed(err)
                ? replaceWords(err)
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useChangeVehicleOwnership(customerId?: number) {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: ChangeVehicleOwnershipRequestInterface) =>
      changeCustomerVehicleOwnership(obj),
    {
      async onSuccess() {
        notification.success({
          message: 'Veículo alterado com sucesso!',
          description: 'O veículo foi atribuído a este cliente.',
        });

        if (customerId) {
          queryChanel({
            invalidateQueries: [
              ['customers', customerId, 'vehicles'],
              ['customers/vehicles/filter', customerId],
            ],
            resetQueries: [
              ['customer/filter/reduced'],
              ['customers', customerId, 'vehicles'],
              ['customer/filter'],
              ['customer/detail/reduced'],
            ],
          });
        }
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useVehicleEnrich() {
  const queryChanel = useQueryChanel();

  return useMutation(enrichCustomerVehicle, {
    async onSuccess(_, vars) {
      notification.success({
        message: 'Veículo atualizado com sucesso!',
        description: 'Os dados do veículo foram atualizados com sucesso.',
      });

      if (vars.customerId) {
        queryChanel({
          invalidateQueries: [
            ['customers', vars.customerId, 'vehicles'],
            ['customers/vehicles/filter', vars.customerId],
            ['customers', vars.customerId, 'vehicles', vars.vehicleId],
          ],
          resetQueries: [
            ['customer/filter/reduced'],
            ['customers', vars.customerId, 'vehicles'],
            ['customer/filter'],
            ['customer/detail/reduced'],
          ],
        });
      }
    },
    onError(error) {
      notificationError(error);
    },
  });
}
